import { Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import {
  ButtonComponent,
  CheckboxComponent,
  IconButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  ModalService,
  SkeletonDirective,
  SvgIconComponent,
} from '@ui-lib/components';
import {
  IPermission,
  IPermissionGroup,
  IRoleModalData,
  IRoleModalFormChecker,
  RolesAction,
} from '../../roles.model';
import { TranslateModule } from '@ngx-translate/core';
import { RolePermissionsService } from '../../services/role-permissions.service';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RoleManagementService } from '../../services/role-management.service';
import { UserInformationService } from '../../../../core/services/user/user-information.service';
import { filter, forkJoin, map, Observable, shareReplay, take } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RoleResponse } from '../../../../core/openapi/identity';
import { RoleActionConfirmationModalComponent } from '../role-action-confirmation-modal/role-action-confirmation-modal.component';
import { UserPermissionEnum } from '../../../../core/models/user-permissions.enum';
import { LoadingState } from '../../../../shared/types/loading.type';

@Component({
  selector: 'mon-role-modal',
  imports: [
    TranslateModule,
    SvgIconComponent,
    CheckboxComponent,
    SkeletonDirective,
    FormsModule,
    AsyncPipe,
    InputComponent,
    ReactiveFormsModule,
    ButtonComponent,
    IconButtonComponent,
  ],
  templateUrl: './role-modal.component.html',
  styleUrl: './role-modal.component.scss',
  providers: [RolePermissionsService, RoleManagementService],
})
export class RoleModalComponent
  extends ModalData<IRoleModalData>
  implements OnInit, IRoleModalFormChecker
{
  private readonly userInformationService = inject(UserInformationService);
  private readonly modalRef = inject(ModalRef<boolean>);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly LoadingState = LoadingState;
  protected readonly roleManagementService: RoleManagementService = inject(RoleManagementService);
  protected readonly rolePermissionsService: RolePermissionsService =
    inject(RolePermissionsService);

  protected readonly permissionGroups: WritableSignal<IPermissionGroup[]> = signal<
    IPermissionGroup[]
  >([]);

  private loadedRole: RoleResponse | null = null;
  private canManageRole = false;

  protected readonly roleNameControl = new FormControl<string>(
    {
      value: '',
      disabled: true,
    },
    {
      nonNullable: true,
      updateOn: 'change',
      validators: [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(20),
        Validators.pattern(/^[А-Яа-яA-Za-z\- ]+$/),
      ],
    },
  );

  public readonly isRoleManagementAllowed$: Observable<boolean> =
    this.userInformationService.userPermissions$.pipe(
      map((permissions) => permissions.includes(UserPermissionEnum.RoleManage)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  private readonly canManageRoles = toSignal(this.isRoleManagementAllowed$, {
    initialValue: false,
  });

  private _isFormModified = false;

  public get isFormModified(): boolean {
    return this._isFormModified;
  }

  public get isSystemRole(): boolean {
    return !!this.loadedRole?.isSystem;
  }

  public get roleName(): string {
    return this.roleNameControl.value;
  }

  public get isCreatingMode(): boolean {
    return this.modalData.action === RolesAction.Create;
  }

  public get isEditingMode(): boolean {
    return (
      this.modalData.action === RolesAction.Edit &&
      this.modalData.roleId !== undefined &&
      this.modalData.roleId.length > 0
    );
  }

  public ngOnInit(): void {
    this.isRoleManagementAllowed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((canManageRole) => {
        this.canManageRole = canManageRole;
        this.updateControlState(this.roleNameControl, false);
      });

    this.roleNameControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this._isFormModified = true;
      });

    if (this.isEditingMode && typeof this.modalData.roleId === 'string') {
      forkJoin([
        this.rolePermissionsService.loadPermissions(),
        this.roleManagementService.loadRoleDetails(this.modalData.roleId),
      ])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(([permissions, roleDetails]): void => {
          this.loadedRole = roleDetails;
          this.updateControlState(this.roleNameControl, false);
          this.roleNameControl.setValue(roleDetails.name, { emitEvent: false });
          this.permissionGroups.set(
            this.rolePermissionsService.selectRolePermissions(
              roleDetails.permissionIds ?? [],
              permissions,
            ),
          );
        });
    } else {
      this.rolePermissionsService
        .loadPermissions()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((permissions) => this.permissionGroups.set(permissions));
    }
  }

  public toggleVisibleGroup(group: IPermissionGroup): void {
    this.permissionGroups.update((groups) =>
      groups.map((g) => (g.id === group.id ? { ...g, hidden: !g.hidden } : g)),
    );
  }

  public togglePermissionGroup(group: IPermissionGroup): void {
    if (!this.canManageRoles() || this.isSystemRole) {
      return;
    }

    this._isFormModified = true;
    this.permissionGroups.set(
      this.rolePermissionsService.togglePermissionGroup(this.permissionGroups(), group),
    );
  }

  public togglePermission(permissionGroup: IPermissionGroup, permission: IPermission): void {
    if (!this.canManageRoles() || this.isSystemRole) {
      return;
    }

    this._isFormModified = true;
    this.permissionGroups.set(
      this.rolePermissionsService.togglePermission(
        this.permissionGroups(),
        permissionGroup,
        permission,
      ),
    );
  }

  public saveChanges(): void {
    const selectedIds = this.getSelectedPermissionIds();

    if (
      !this.canManageRoles() ||
      this.isSystemRole ||
      this.roleNameControl.invalid ||
      selectedIds.length === 0
    ) {
      return;
    }

    switch (this.modalData.action) {
      case RolesAction.Create: {
        this.roleManagementService
          .createRole(this.roleName, selectedIds)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((): void => {
            this._isFormModified = false;
            this.modalRef.close(true);
          });
        break;
      }
      case RolesAction.Edit: {
        this.roleManagementService
          .updateRole(
            this.loadedRole?.id ?? this.modalData.roleId ?? '',
            this.roleName,
            selectedIds,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((): void => {
            this._isFormModified = false;
            this.modalRef.close(true);
          });
        break;
      }
      default: {
        throw new Error('Unsupported action');
      }
    }
  }

  protected getSelectedPermissionIds(): string[] {
    return this.rolePermissionsService.getSelectedPermissionIds(this.permissionGroups());
  }

  protected deleteRole(): void {
    if (
      !this.canManageRoles() ||
      this.roleManagementService.loadingState() === LoadingState.LOADING
    ) {
      return;
    }

    this.modalService
      .open<void>({
        component: RoleActionConfirmationModalComponent,
        withoutBackdropClass: true,
        customContainerClass: 'role-deletion-modal',
      })
      .afterClose$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe((result) => {
        this.roleManagementService
          .deleteRole(this.loadedRole?.id ?? this.modalData.roleId ?? '', this.roleName)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.modalRef.close(result);
          });
      });
  }

  private updateControlState(control: AbstractControl, emitEvent = true): void {
    if (!this.canManageRole || this.isRoleDetailsLoading() || this.isSystemRole) {
      control.disable({ emitEvent });
    } else {
      control.enable({ emitEvent });
    }
  }

  private isRoleDetailsLoading(): boolean {
    return this.isEditingMode && this.loadedRole === null;
  }
}
